import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { FlaskConical, Thermometer, Clock, AlertCircle, CheckCircle } from 'lucide-react';

function CrucibleReport() {
  const { API } = useContext(AppContext);
  const [crucibleData, setCrucibleData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCrucibleData();
  }, []);

  const fetchCrucibleData = async () => {
    // Mock data for demonstration
    setCrucibleData([
      { id: 'CR-001', name: 'Crucible A1', status: 'Active', temperature: 1250, cycles: 45, maxCycles: 50, lastMaintenance: '2025-01-28', health: 'Good' },
      { id: 'CR-002', name: 'Crucible A2', status: 'Active', temperature: 1245, cycles: 38, maxCycles: 50, lastMaintenance: '2025-01-25', health: 'Good' },
      { id: 'CR-003', name: 'Crucible B1', status: 'Maintenance', temperature: 0, cycles: 50, maxCycles: 50, lastMaintenance: '2025-02-01', health: 'Needs Replacement' },
      { id: 'CR-004', name: 'Crucible B2', status: 'Active', temperature: 1248, cycles: 22, maxCycles: 50, lastMaintenance: '2025-01-20', health: 'Excellent' },
      { id: 'CR-005', name: 'Crucible C1', status: 'Standby', temperature: 800, cycles: 30, maxCycles: 50, lastMaintenance: '2025-01-22', health: 'Good' },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-700';
      case 'Maintenance': return 'bg-red-100 text-red-700';
      case 'Standby': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'Excellent': return 'text-green-600';
      case 'Good': return 'text-blue-600';
      case 'Needs Replacement': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading crucible data...</div>;
  }

  const activeCount = crucibleData.filter(c => c.status === 'Active').length;
  const maintenanceCount = crucibleData.filter(c => c.status === 'Maintenance').length;

  return (
    <div className="space-y-6" data-testid="crucible-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crucible Report</h1>
          <p className="text-gray-600 mt-1">Monitor crucible status and performance</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Crucibles</p>
              <p className="text-2xl font-bold text-gray-900">{crucibleData.length}</p>
            </div>
            <FlaskConical className="text-blue-500" size={32} />
          </div>
        </div>
        <div className="bg-green-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Active</p>
              <p className="text-2xl font-bold text-green-700">{activeCount}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        <div className="bg-red-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Maintenance</p>
              <p className="text-2xl font-bold text-red-700">{maintenanceCount}</p>
            </div>
            <AlertCircle className="text-red-500" size={32} />
          </div>
        </div>
        <div className="bg-orange-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600">Avg Temperature</p>
              <p className="text-2xl font-bold text-orange-700">1248°C</p>
            </div>
            <Thermometer className="text-orange-500" size={32} />
          </div>
        </div>
      </div>

      {/* Crucible Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {crucibleData.map((crucible) => (
          <div key={crucible.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500 font-mono">{crucible.id}</p>
                <h3 className="text-lg font-bold text-gray-900">{crucible.name}</h3>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(crucible.status)}`}>
                {crucible.status}
              </span>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Temperature</span>
                <span className="text-sm font-medium text-gray-900">
                  {crucible.temperature > 0 ? `${crucible.temperature}°C` : '-'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Cycles</span>
                <span className="text-sm font-medium text-gray-900">{crucible.cycles} / {crucible.maxCycles}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${crucible.cycles / crucible.maxCycles > 0.9 ? 'bg-red-500' : 'bg-blue-500'}`}
                  style={{ width: `${(crucible.cycles / crucible.maxCycles) * 100}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Health</span>
                <span className={`text-sm font-medium ${getHealthColor(crucible.health)}`}>{crucible.health}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last Maintenance</span>
                <span className="text-sm text-gray-900">{crucible.lastMaintenance}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CrucibleReport;
